// Pure pagination math, kept React-free so the slicing + clamping is unit-
// testable (the app has no DOM test environment). `page` is 1-based.

export type Pagination<T> = {
  /** Items belonging to the (clamped) current page. */
  pageItems: T[];
  /** The page actually shown, clamped to [1, totalPages]. */
  page: number;
  totalPages: number;
  /** 1-based index of the first item on the page (0 when empty). */
  from: number;
  /** 1-based index of the last item on the page (0 when empty). */
  to: number;
  total: number;
};

/**
 * Slice `items` into a single page. The requested `page` is floored and clamped
 * into [1, totalPages] so a stale page number (e.g. after the list shrank from
 * filtering) never yields an empty view — it lands on the last available page.
 */
export function paginate<T>(items: T[], page: number, pageSize: number): Pagination<T> {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const clamped = Math.min(Math.max(1, Math.floor(page)), totalPages);
  const start = (clamped - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);
  return {
    pageItems,
    page: clamped,
    totalPages,
    from: total === 0 ? 0 : start + 1,
    to: start + pageItems.length,
    total,
  };
}
